import { useEffect, useState } from 'react';
import { CreateForm, FormRowType } from 'framework/form';
import { FormBuilder, type FormInputs } from '../../core/FormBuilder';
import useOneStopService from '../useOneStopService';
import type { Request } from '../oneStopTypes';
import useOneStopConfiguration from '../useOneStopConfiguration';
import { useFeedbackRequestUrl } from '../route/requests';
import OneStopModalDecorator from '../OneStopModalDecorator';
import { Modal } from 'framework/ui/ui-builder';
import OneStopRequestLayout from '../OneStopRequestLayout';
import { htmlToTextile } from 'framework/utils';
import { Link } from '@mui/material';

const MESSAGE_CONTEXT = 'feedbackForm';

type FeedbackFormProps = {
  formId: string;
  formTitle: string;
  closeFeedbackForm: () => void;
  setIsLoaderVisible: (isLoading: boolean) => void;
  technicalDescription?: string;
  defaultValues?: Dictionary<any>;
};

const FeedbackForm = ({
  formId,
  formTitle,
  closeFeedbackForm,
  setIsLoaderVisible,
  technicalDescription,
  defaultValues: defaultValuesProps = {},
}: FeedbackFormProps) => {
  const [request, setRequest] = useState<Nullable<Request>>(null);
  const [formConfiguration, setFormConfiguration] = useState<Nullable<FormInputs>>(null);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [isLoading, setLoading] = useState<Boolean>(true);
  const { getById, sendRequest, getFieldSourcesForHtmlFields, getFormattedData } = useOneStopService({
    messageContext: MESSAGE_CONTEXT,
  });
  const { getRequestConfiguration, getFormConfiguration, getValidationSchema } = useOneStopConfiguration({
    getById,
    id: formId,
    rowType: FormRowType.BIG,
  });
  const { supportUrl } = useFeedbackRequestUrl(formId);

  // used <p> tag instead of \n because we pass this value to the ckeditor(wysiwyg)
  const defaultValues = {
    description: technicalDescription
      ? ''
      : `<p></p><p></p><p></p><p></p><p></p><p></p><p>Submitted from: ${window.location.origin}${window.location.pathname}</p>`,
    ...defaultValuesProps,
  };

  useEffect(() => {
    Promise.all([
      getRequestConfiguration().then(data => setRequest(data)),
      getFormConfiguration().then(data => setFormConfiguration(data)),
    ])
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsLoaderVisible(Boolean(isLoading));
  }, [isLoading, setIsLoaderVisible]);

  if (isLoading) {
    return null;
  }

  const closeFeedbackModal = () => setIsModalOpen(false);

  const headerRight = (
    <Link href={supportUrl} target="_blank" rel="noopener noreferrer" my={0.5} mx={0}>
      Open {formTitle} form in a new tab
    </Link>
  );

  const htmlFieldSources = formConfiguration ? getFieldSourcesForHtmlFields(formConfiguration) : [];

  return (
    <OneStopModalDecorator
      isOneStop={false}
      submitHandler={closeFeedbackModal}
      handleMessageModalClose={closeFeedbackForm}
      messageContext={MESSAGE_CONTEXT}
    >
      <CreateForm
        initialData={defaultValues}
        getValidationSchemaFunc={getValidationSchema}
        submitFormFunc={(data: any) => {
          const formattedData = getFormattedData(data, htmlFieldSources);
          if (!technicalDescription) {
            return sendRequest(formId, formattedData);
          }

          const technicalDescriptionText = htmlToTextile(technicalDescription) || '';
          if (!technicalDescriptionText) {
            return sendRequest(formId, formattedData);
          }

          const description = formattedData?.description || '';
          const separator = description ? '\n\n' : '';

          return sendRequest(formId, {
            ...formattedData,
            description: `${description}${separator}${technicalDescriptionText}`,
          });
        }}
        messageContext={MESSAGE_CONTEXT}
      >
        <Modal
          key="modal-not-strict"
          modalSize="sm"
          backdrop="static"
          keyboard={false}
          isOpen={isModalOpen}
          hasCancelButton={false}
          title={request ? null : ' '}
          onToggle={closeFeedbackForm}
        >
          {request && formConfiguration ? (
            <OneStopRequestLayout
              request={request}
              handleCancelClick={closeFeedbackForm}
              headerRight={headerRight}
              isFixedButtons={false}
            >
              <FormBuilder formInputs={formConfiguration} />
            </OneStopRequestLayout>
          ) : null}
        </Modal>
      </CreateForm>
    </OneStopModalDecorator>
  );
};

export default FeedbackForm;
